import BaseApi from '@core/api/model';

import RatePlanStatusEnum from '@constants/enums/ratePlan/status';

import { previewRatePlanValue } from '@packages/rate-plans/utils/previewRatePlanValue';

import { wait } from '@utils/async';

export class RatePlanApi extends BaseApi {
    static resource = 'rate-plans';
    static mockItemCount = 6;
    static mockUseFilterOverrides = true;
    static mockFilterOverrideKeys = ['adviser_id', 'type', 'status'];

    get endpoints() {
        return {
            ...super.endpoints,
            checkValue: this.checkValue.bind(this),
            activate: this.activate.bind(this),
            deactivate: this.deactivate.bind(this),
            deprecate: this.deprecate.bind(this),
            makeDefault: this.makeDefault.bind(this)
        };
    }

    get mock_endpoints() {
        return {
            ...super.mock_endpoints,
            checkValue: this.checkValue_mock.bind(this),
            activate: this.activate_mock.bind(this),
            deactivate: this.deactivate_mock.bind(this),
            deprecate: this.deprecate_mock.bind(this),
            makeDefault: this.makeDefault_mock.bind(this)
        };
    }

    _normaliseRatePlanItem(item = {}) {
        if (!item || typeof item !== 'object') {
            return item;
        }

        return {
            ...item,
            // The real API nests the adviser as adviser: {id, name, gi_ref} — there is no flat
            // adviser_id field on any rate-plan response. Every adviser_id filter/comparison in
            // this codebase reads the flat field, so it has to be derived here or it's always null.
            adviser_id: item.adviser_id ?? item.adviser?.id ?? null,
            default: Boolean(item.default),
            entries: Array.isArray(item.entries) ? item.entries : []
        };
    }

    _normaliseRatePlanData(data) {
        if (Array.isArray(data)) {
            return data.map(item => this._normaliseRatePlanItem(item));
        }

        return this._normaliseRatePlanItem(data);
    }

    /**
     * Attach list/detail statistics for mocks (subscription_count + is_only_active_of_type).
     * @param {object[]} items
     * @returns {object[]}
     */
    _withMockStatistics(items = []) {
        const activeCountByFirmType = {};

        items.forEach(item => {
            if (!item || item.status !== RatePlanStatusEnum.ACTIVE || !item.type) {
                return;
            }

            const firmType = `${item.adviser_id || ''}:${item.type}`;

            /* eslint-disable-next-line security/detect-object-injection -- key built from our mock data */
            activeCountByFirmType[firmType] = (activeCountByFirmType[firmType] || 0) + 1;
        });

        return items.map(item => {
            if (!item || typeof item !== 'object') {
                return item;
            }

            const subscriptionCount = item.subscription_count != null ? item.subscription_count : 0;
            const firmType = `${item.adviser_id || ''}:${item.type}`;

            /* eslint-disable-next-line security/detect-object-injection -- key built from our mock data */
            const activeOfFirmType = item.type ? activeCountByFirmType[firmType] || 0 : 0;

            return {
                ...item,
                subscription_count: subscriptionCount,
                is_only_active_of_type:
                    item.is_only_active_of_type != null
                        ? item.is_only_active_of_type
                        : item.status === RatePlanStatusEnum.ACTIVE && activeOfFirmType <= 1,
                mock_has_subscriptions:
                    item.mock_has_subscriptions != null ? item.mock_has_subscriptions : Number(subscriptionCount) > 0
            };
        });
    }

    _enrichMockDetailStatistics(item) {
        if (!item || typeof item !== 'object') {
            return item;
        }

        const peers = this._withMockStatistics(this._normaliseRatePlanData(this._getMocked()));
        const enrichedPeer = peers.find(plan => plan.id === item.id);

        if (enrichedPeer) {
            return this._normaliseRatePlanItem({
                ...item,
                subscription_count: enrichedPeer.subscription_count,
                is_only_active_of_type: enrichedPeer.is_only_active_of_type,
                mock_has_subscriptions: enrichedPeer.mock_has_subscriptions
            });
        }

        return this._withMockStatistics([item])[0];
    }

    _withExplicitStatistics(item) {
        if (!item || typeof item !== 'object') {
            return item;
        }

        return {
            ...item,
            subscription_count: item.subscription_count != null ? item.subscription_count : null,
            is_only_active_of_type: item.is_only_active_of_type != null ? item.is_only_active_of_type : null
        };
    }

    _filterGetResponseData(data) {
        const normalised = this._normaliseRatePlanData(data);

        if (!normalised || Array.isArray(normalised)) {
            return normalised;
        }

        // Mock statistics are fabricated for the mock layer only. Real GET
        // still always writes the stat keys (null when omitted) so Vuex ORM
        // insertOrUpdate cannot keep stale list statistics on the detail.
        if (!BaseApi.shouldMockApi()) {
            return this._withExplicitStatistics(normalised);
        }

        return this._withExplicitStatistics(this._enrichMockDetailStatistics(normalised));
    }

    _filterIndexResponseData(data) {
        return this._normaliseRatePlanData(data);
    }

    /**
     * Retain unknown override keys (e.g. mock_has_subscriptions) on generated mocks.
     * Schema-driven BaseApi._generateMocked only applies known RatePlan.mock fields.
     */
    async _generateMocked(limit = 1, overrides = {}) {
        const generated = await super._generateMocked(limit, overrides);

        if (!generated.length || !Object.keys(overrides).length) {
            return generated;
        }

        const withOverrides = generated.map(item => ({ ...item, ...overrides }));
        const generatedIds = new Set(generated.map(item => item.id));
        const items = this._getMocked().map(item => {
            if (!generatedIds.has(item.id)) {
                return item;
            }

            return { ...item, ...overrides };
        });

        this._saveMocked(items);

        return withOverrides;
    }

    _findMockItem(id) {
        const items = this._getMocked();
        const index = items.findIndex(item => item.id === id);

        if (index === -1) {
            return { items, index: -1, item: null };
        }

        /* eslint-disable-next-line security/detect-object-injection -- index from findIndex on our mock array */
        return { items, index, item: items[index] };
    }

    _lifecycleSuccess(item) {
        const data = this._normaliseRatePlanItem(item);
        const response = { status: 'success', data };

        return this._respond(response, () => {
            this._updateItemInCollections(data.id, data);
            this._insertModel(response.data, this.ApiResponseFormatEnum.DETAIL);
        });
    }

    async _postLifecycle(id, action) {
        const response = await BaseApi.api.post(`${this.base_url}/${id}/${action}`, null);

        return this._respond(response, () => {
            response.data = this._normaliseRatePlanItem(this._filterResponseData(response.data));
            this._updateItemInCollections(id, response.data);
            this._insertModel(response.data, this.ApiResponseFormatEnum.DETAIL);
        });
    }

    /**
     * Preview the annual fee for a draft or saved rate plan.
     * @param {object} payload {@link CheckValueSchema}: `plan` (inline RatePlanDefinitionSchema) or
     *   `rate_plan_id`, plus exactly one of `value`, `value_breakdown` or `quantity`.
     * @param {object} config
     */
    async checkValue(payload = {}, config = {}) {
        const response = await BaseApi.api.post(`${this.base_url}/check-value`, payload, config);

        return this._respond(response, () => {
            response.data = this._filterResponseData(response.data);
        });
    }

    async checkValue_mock(payload = {}) {
        await wait(150);

        return {
            status: 'success',
            data: previewRatePlanValue(payload)
        };
    }

    async activate(id) {
        return this._postLifecycle(id, 'activate');
    }

    async deactivate(id) {
        return this._postLifecycle(id, 'deactivate');
    }

    async deprecate(id) {
        return this._postLifecycle(id, 'deprecate');
    }

    async makeDefault(id) {
        return this._postLifecycle(id, 'make-default');
    }

    async activate_mock(id) {
        await wait(150);
        const { items, index, item } = this._findMockItem(id);

        if (!item) {
            return { status: 'fail', message: 'Not found' };
        }

        const updated = this._normaliseRatePlanItem({ ...item, status: RatePlanStatusEnum.ACTIVE });
        /* eslint-disable-next-line security/detect-object-injection -- index from findIndex */
        items[index] = updated;
        this._saveMocked(items);

        return this._lifecycleSuccess(updated);
    }

    async deactivate_mock(id) {
        await wait(150);
        const { items, index, item } = this._findMockItem(id);

        if (!item) {
            return { status: 'fail', message: 'Not found' };
        }

        if (item.default) {
            return {
                status: 'fail',
                message: 'The default rate plan cannot be deactivated.'
            };
        }

        if (item.mock_has_subscriptions) {
            return {
                status: 'fail',
                message: 'This rate plan cannot be deactivated because it has been used on a subscription.'
            };
        }

        const updated = this._normaliseRatePlanItem({ ...item, status: RatePlanStatusEnum.DRAFT });
        /* eslint-disable-next-line security/detect-object-injection -- index from findIndex */
        items[index] = updated;
        this._saveMocked(items);

        return this._lifecycleSuccess(updated);
    }

    async deprecate_mock(id) {
        await wait(150);
        const { items, index, item } = this._findMockItem(id);

        if (!item) {
            return { status: 'fail', message: 'Not found' };
        }

        if (item.default) {
            return {
                status: 'fail',
                message: 'The default rate plan cannot be deprecated.'
            };
        }

        const activeSameType = items.filter(
            candidate =>
                candidate.status === RatePlanStatusEnum.ACTIVE &&
                candidate.type === item.type &&
                candidate.adviser_id === item.adviser_id
        );

        if (activeSameType.length <= 1 && item.status === RatePlanStatusEnum.ACTIVE) {
            return {
                status: 'fail',
                message:
                    'This rate plan cannot be deprecated because it is the only active plan of its type for the firm.'
            };
        }

        const updated = this._normaliseRatePlanItem({ ...item, status: RatePlanStatusEnum.DEPRECATED });
        /* eslint-disable-next-line security/detect-object-injection -- index from findIndex */
        items[index] = updated;
        this._saveMocked(items);

        return this._lifecycleSuccess(updated);
    }

    async makeDefault_mock(id) {
        await wait(150);
        const { items, index, item } = this._findMockItem(id);

        if (!item) {
            return { status: 'fail', message: 'Not found' };
        }

        if (item.status !== RatePlanStatusEnum.ACTIVE) {
            return {
                status: 'fail',
                message: 'Only active rate plans can be set as default.'
            };
        }

        items.forEach((candidate, candidateIndex) => {
            if (candidate.id !== id && candidate.adviser_id === item.adviser_id && candidate.type === item.type) {
                /* eslint-disable-next-line security/detect-object-injection -- index from forEach */
                items[candidateIndex] = { ...candidate, default: false };
            }
        });

        const updated = this._normaliseRatePlanItem({ ...item, default: true });
        /* eslint-disable-next-line security/detect-object-injection -- index from findIndex */
        items[index] = updated;
        this._saveMocked(items);

        return this._lifecycleSuccess(updated);
    }

    async remove_mock(id) {
        await wait(150);
        const { items, index, item } = this._findMockItem(id);

        if (!item) {
            return { status: 'fail', message: 'Not found' };
        }

        if (item.mock_has_subscriptions) {
            return {
                status: 'fail',
                message: 'This rate plan cannot be deleted because it has subscriptions.'
            };
        }

        items.splice(index, 1);
        this._saveMocked(items);

        const response = { status: 'success' };

        return this._respond(response, () => {
            this._removeItemInCollections(id);
            this.model.delete(id);
        });
    }

    // eslint-disable-next-line no-unused-vars
    async index_mock(offset = 0, limit = 100, sort_by = null, filters = {}) {
        await wait(200);

        const includeStatistics = Boolean(filters?.statistics);
        const rowFilters = { ...(filters || {}) };
        delete rowFilters.statistics;

        let items = this._getMocked();

        items = items.filter(item => {
            if (this.constructor.mockSoftDelete && item.deleted_at) {
                return false;
            }

            return true;
        });

        if (!items.length) {
            items = await this._generateMocked(this.constructor.mockItemCount);
        }

        items = this._normaliseRatePlanData(items);

        let filtered = this._filterMocked(items, rowFilters);

        if (
            this.constructor.mockUseFilterOverrides &&
            !filtered.length &&
            rowFilters &&
            Object.keys(rowFilters).length
        ) {
            const overrides = {};

            for (const key in rowFilters) {
                const value = rowFilters[`${key}`];

                if (
                    this.constructor.mockFilterOverrideKeys.includes(key) &&
                    typeof value === 'string' &&
                    value.length
                ) {
                    const parts = value.split(':');
                    overrides[`${key}`] = parts.length > 1 ? parts.slice(1).join(':') : value;
                }
            }

            if (Object.keys(overrides).length) {
                filtered = this._normaliseRatePlanData(
                    await this._generateMocked(this.constructor.mockItemCount, overrides)
                );
            }
        }

        items = filtered.map(item => {
            if (includeStatistics) {
                return item;
            }

            const rest = { ...item };
            delete rest.subscription_count;
            delete rest.is_only_active_of_type;

            return rest;
        });

        if (includeStatistics) {
            items = this._withMockStatistics(items);
        }

        const sliced = offset !== null && limit !== null ? items.slice(offset, offset + limit) : items;

        const response = {
            status: 'success',
            data: sliced,
            metadata: {
                total: items.length,
                limit,
                offset
            }
        };

        return this._respond(response, () => {
            this._insertModels(response.data);
        });
    }
}

export default RatePlanApi;
